import { useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    TextField,
    Typography,
    Button,
    Snackbar,
    Alert,
    CircularProgress,
    ThemeProvider,
    createTheme,
    IconButton,
    ButtonBase,
    Skeleton,
    Dialog,
    DialogContent,
    MenuItem,
    Select,
    FormControl,
    Tooltip
} from "@mui/material";
import { Grid } from "@mui/system";
import axios from 'axios';
import PhoneInput from 'react-phone-number-input';
import { isValidPhoneNumber } from 'libphonenumber-js';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import RefreshIcon from '@mui/icons-material/Refresh';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { makeGetRequestToUrl } from '../commonFunc.jsx';
import { useNavigate } from "react-router-dom";
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import 'react-phone-number-input/style.css';
import config from "../../config/config.js";

// ---- Endpoints & constants -------------------------------------------------
// TODO: move these into config.js once the final hosts are decided
const BUSINESS_TOKEN = 'ZFOGFQKCi1X8GxjkC4RV2O04dAeT19zO';
const GET_DOCUMENTS_URL = 'https://localhost:8446';
const REGISTER_URL = 'https://localhost:8445/fleet/register_fleet';
const MAX_FILE_MB = 5;

// ---- Palette ---------------------------------------------------------------

const c = {
    asphalt: '#121519',
    asphaltDeep: '#0A0C0F',
    road: '#2A3038',
    onDark: '#FFFFFF',
    onDarkMuted: 'rgba(255,255,255,0.6)',
    onDarkLine: 'rgba(255,255,255,0.1)',
    page: '#F2F3F0',
    surface: '#FFFFFF',
    line: '#E1E4E8',
    lineStrong: '#C3C8CF',
    ink: '#15191F',
    inkSoft: '#5E6672',
    amber: '#F2B33D',
    amberDeep: '#B9831C',
    amberSoft: '#FEF5E1',
    go: '#2FA866',
    goSoft: '#E6F5EC',
    error: '#C2413B',
    errorSoft: '#FBECEB',
};

const theme = createTheme({
    palette: { primary: { main: c.asphalt }, error: { main: c.error } },
    typography: { fontFamily: '"DM Sans", "Helvetica Neue", Arial, sans-serif' },
    shape: { borderRadius: 6 },
    components: {
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    backgroundColor: c.surface,
                    fontSize: '15px',
                    color: c.ink,
                    borderRadius: 6,
                    '& fieldset': { borderColor: c.line },
                    '&:hover fieldset': { borderColor: c.lineStrong },
                    '&.Mui-focused fieldset': { borderColor: c.asphalt, borderWidth: '1px' },
                    '&.Mui-error fieldset': { borderColor: c.error },
                },
                input: { padding: '13px 14px' },
            },
        },
        MuiFormHelperText: { styleOverrides: { root: { marginLeft: 0, fontSize: '12.5px' } } },
    },
});

const useFonts = () => {
    useEffect(() => {
        if (document.getElementById('fleet-onboarding-fonts')) return;
        const link = document.createElement('link');
        link.id = 'fleet-onboarding-fonts';
        link.rel = 'stylesheet';
        link.href = 'https://fonts.googleapis.com/css2?family=Archivo:wght@500;600;700&family=DM+Sans:wght@400;500;600&family=JetBrains+Mono:wght@500&display=swap';
        document.head.appendChild(link);
    }, []);
};

const display = '"Archivo", "Helvetica Neue", Arial, sans-serif';
const mono = '"JetBrains Mono", ui-monospace, Menlo, monospace';
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const STEPS = [
    { label: 'Details', title: 'Tell us about your fleet', description: 'We\u2019ll use these to contact you about your registration.' },
    { label: 'Documents', title: 'City and documents', description: 'Pick the city you operate in. The documents you need depend on it.' },
];

// ---- Document helpers (field names mirror driver documents) ---------------

const slotsFor = (doc) => Math.max(1, Number(doc.no_of_images) || 1);
const isRequired = (doc) => Number(doc.is_required) === 1;
const needsNumber = (doc) => Number(doc.is_doc_number_required) === 1;
const needsExpiry = (doc) => Number(doc.is_doc_expired) === 1;
const cameraOnly = (doc) => Number(doc.is_gallery_restricted) === 1;

const emptyDocState = (doc) => ({
    files: Array(slotsFor(doc)).fill(null),
    previews: Array(slotsFor(doc)).fill(null),
    number: '',
    expiry: '',
});

const isDocStarted = (st) => st && (st.files.some(Boolean) || st.number.trim() || st.expiry);

const getDocError = (doc, st) => {
    if (!isRequired(doc) && !isDocStarted(st)) return null;
    const missing = st.files.filter((f) => !f).length;
    if (missing) return slotsFor(doc) > 1 ? `Upload all ${slotsFor(doc)} images` : 'Upload an image of this document';
    if (needsNumber(doc) && !st.number.trim()) return 'Enter the document number';
    if (needsExpiry(doc) && !st.expiry) return 'Enter the expiry date';
    return null;
};

const isDocComplete = (doc, st) => st && st.files.every(Boolean) && !getDocError(doc, st);

// ---- Building blocks -------------------------------------------------------

const FieldLabel = ({ children, required, hint }) => (
    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: '7px' }}>
        <Typography component="label" sx={{ fontSize: '13px', fontWeight: 500, color: c.ink }}>
            {children}
            {required && <Box component="span" sx={{ color: c.amberDeep, ml: '3px' }}>*</Box>}
        </Typography>
        {hint && <Typography sx={{ fontSize: '12px', color: c.inkSoft }}>{hint}</Typography>}
    </Box>
);

const ErrorText = ({ children }) => children
    ? <Typography sx={{ color: c.error, fontSize: '12.5px', mt: '6px' }}>{children}</Typography>
    : null;

const RouteStepper = ({ current, onJump }) => {
    const pct = (current / (STEPS.length - 1)) * 100;
    const ease = 'cubic-bezier(0.2, 0.8, 0.2, 1)';
    return (
        <Box sx={{ pt: 5, px: { xs: '14px', sm: '18px' }, maxWidth: 560 }}>
            <Box sx={{ position: 'relative', height: 12, borderRadius: 6, bgcolor: c.road }}>
                <Box sx={{
                    position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`, borderRadius: 6,
                    bgcolor: 'rgba(242,179,61,0.25)', transition: `width 500ms ${ease}`,
                }} />
                <Box sx={{
                    position: 'absolute', left: 10, right: 10, top: '50%', height: 2, mt: '-1px', opacity: 0.6,
                    background: `repeating-linear-gradient(to right, ${c.amber} 0 14px, transparent 14px 28px)`,
                }} />
                <Box aria-hidden sx={{
                    position: 'absolute', top: 0, left: `${pct}%`,
                    transform: 'translate(-50%, -125%)', transition: `left 500ms ${ease}`,
                    bgcolor: c.amber, borderRadius: '6px', px: '7px', py: '2px', display: 'flex',
                    boxShadow: '0 6px 14px -6px rgba(242,179,61,0.7)',
                    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                }}>
                    <DirectionsCarIcon sx={{ fontSize: 18, color: c.asphalt }} />
                </Box>
                {STEPS.map((s, i) => {
                    const done = i < current;
                    const active = i === current;
                    return (
                        <ButtonBase
                            key={s.label}
                            disabled={i > current}
                            onClick={() => onJump(i)}
                            aria-label={`Step ${i + 1}: ${s.label}`}
                            aria-current={active ? 'step' : undefined}
                            sx={{
                                position: 'absolute', top: '50%', left: `${(i / (STEPS.length - 1)) * 100}%`,
                                transform: 'translate(-50%, -50%)', width: 28, height: 28, borderRadius: '50%',
                                fontFamily: mono, fontSize: '12px',
                                bgcolor: done ? c.go : active ? c.amber : c.asphalt,
                                color: done ? '#fff' : active ? c.asphalt : c.onDarkMuted,
                                border: `2px solid ${done ? c.go : active ? c.amber : c.road}`,
                                transition: 'background-color 200ms ease, border-color 200ms ease',
                                '&.Mui-focusVisible': { outline: `2px solid ${c.amber}`, outlineOffset: '3px' },
                            }}
                        >
                            {done ? <CheckIcon sx={{ fontSize: 15 }} /> : i + 1}
                        </ButtonBase>
                    );
                })}
            </Box>
            <Box sx={{ position: 'relative', height: 34, mt: '14px' }}>
                {STEPS.map((s, i) => {
                    const left = (i / (STEPS.length - 1)) * 100;
                    const align = i === 0 ? 'translateX(-14px)' : i === STEPS.length - 1 ? 'translateX(calc(-100% + 14px))' : 'translateX(-50%)';
                    return (
                        <Typography key={s.label} sx={{
                            position: 'absolute', left: `${left}%`, transform: align, whiteSpace: 'nowrap',
                            fontSize: '13px', fontWeight: i === current ? 600 : 400,
                            color: i <= current ? c.onDark : c.onDarkMuted,
                        }}>
                            {s.label}
                        </Typography>
                    );
                })}
            </Box>
        </Box>
    );
};

// ---- City picker -----------------------------------------------------------

const CityCard = ({ city, selected, onClick }) => {
    const count = (city.fleet_required_documents || []).length;
    return (
        <ButtonBase
            onClick={onClick}
            aria-pressed={selected}
            sx={{
                position: 'relative', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left',
                p: '14px 16px', borderRadius: '8px', width: '100%',
                bgcolor: selected ? c.asphalt : c.surface,
                border: `1px solid ${selected ? c.asphalt : c.line}`,
                transition: 'background-color 150ms ease, border-color 150ms ease',
                '&:hover': { borderColor: selected ? c.asphalt : c.lineStrong },
                '&.Mui-focusVisible': { outline: `2px solid ${c.amber}`, outlineOffset: '2px' },
            }}
        >
            {selected && (
                <Box sx={{
                    position: 'absolute', top: 10, right: 10, width: 18, height: 18, borderRadius: '50%',
                    bgcolor: c.amber, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <CheckIcon sx={{ fontSize: 12, color: c.asphalt }} />
                </Box>
            )}
            <Typography sx={{ fontFamily: display, fontSize: '16px', fontWeight: 600, color: selected ? '#fff' : c.ink, pr: 3 }}>
                {city.city_name}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, mt: '6px' }}>
                <Typography sx={{ fontFamily: mono, fontSize: '11.5px', color: selected ? c.amber : c.inkSoft }}>
                    {city.currency}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: selected ? c.onDarkMuted : c.inkSoft }}>
                    {count === 0 ? 'No documents' : `${count} document${count > 1 ? 's' : ''}`}
                </Typography>
            </Box>
        </ButtonBase>
    );
};

// ---- Document upload -------------------------------------------------------

const UploadSlot = ({ doc, index, total, file, preview, onSelect, onRemove, hasError }) => {
    const [dragOver, setDragOver] = useState(false);
    const camera = cameraOnly(doc);
    const slotLabel = total === 2 ? (index === 0 ? 'Front' : 'Back') : total > 1 ? `Image ${index + 1}` : 'Document image';
    const inputId = `doc-${doc.document_id}-${index}`;

    const fileInput = (
        <input
            id={inputId}
            type="file"
            accept="image/*"
            capture={camera ? 'environment' : undefined}
            hidden
            onChange={(e) => { onSelect(e.target.files?.[0]); e.target.value = ''; }}
        />
    );

    if (file && preview) {
        return (
            <Box sx={{ position: 'relative', borderRadius: '8px', overflow: 'hidden', border: `1px solid ${c.line}`, aspectRatio: '4 / 3', bgcolor: c.page }}>
                <Box component="img" src={preview} alt={`${doc.document_name} ${slotLabel}`}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                <Box sx={{
                    position: 'absolute', inset: 'auto 0 0 0', px: 1.25, py: 1,
                    background: 'linear-gradient(to top, rgba(10,12,15,0.85), rgba(10,12,15,0))',
                    display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 1,
                }}>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: '11px', color: c.amber, fontFamily: mono }}>{slotLabel.toUpperCase()}</Typography>
                        <Typography sx={{ fontSize: '12px', color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {file.name}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                        <IconButton component="label" htmlFor={inputId} size="small" aria-label={`Replace ${slotLabel}`}
                            sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', width: 30, height: 30, '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' } }}>
                            <SwapHorizIcon sx={{ fontSize: 17 }} />
                            {fileInput}
                        </IconButton>
                        <IconButton size="small" onClick={onRemove} aria-label={`Remove ${slotLabel}`}
                            sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', width: 30, height: 30, '&:hover': { bgcolor: c.error } }}>
                            <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                    </Box>
                </Box>
            </Box>
        );
    }

    const Icon = camera ? PhotoCameraOutlinedIcon : CloudUploadOutlinedIcon;
    return (
        <Box
            component="label"
            htmlFor={inputId}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); onSelect(e.dataTransfer.files?.[0]); }}
            sx={{
                aspectRatio: '4 / 3', borderRadius: '8px', cursor: 'pointer',
                border: `1.5px dashed ${hasError ? c.error : dragOver ? c.amberDeep : c.lineStrong}`,
                bgcolor: hasError ? c.errorSoft : dragOver ? c.amberSoft : c.page,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px',
                textAlign: 'center', px: 1.5,
                transition: 'background-color 150ms ease, border-color 150ms ease',
                '&:hover': { borderColor: c.asphalt, bgcolor: '#ECEDEA' },
                '&:focus-within': { outline: `2px solid ${c.amber}`, outlineOffset: '2px' },
            }}
        >
            {fileInput}
            <Box sx={{
                width: 36, height: 36, borderRadius: '50%', bgcolor: c.surface, border: `1px solid ${c.line}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                <Icon sx={{ fontSize: 19, color: c.ink }} />
            </Box>
            <Typography sx={{ fontSize: '13px', fontWeight: 500, color: c.ink }}>
                {camera ? 'Take photo' : 'Upload image'}
            </Typography>
            <Typography sx={{ fontSize: '11.5px', color: c.inkSoft }}>
                {slotLabel} · max {MAX_FILE_MB} MB
            </Typography>
        </Box>
    );
};

const DocumentCard = ({ doc, index, state, error, onSelectFile, onRemoveFile, onField }) => {
    const total = slotsFor(doc);
    const complete = isDocComplete(doc, state);
    return (
        <Box sx={{
            border: `1px solid ${error ? c.error : complete ? c.go : c.line}`, borderRadius: '10px', overflow: 'hidden',
            transition: 'border-color 160ms ease',
        }}>
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5,
                bgcolor: complete ? c.goSoft : '#FAFAF8', borderBottom: `1px solid ${c.line}`,
                transition: 'background-color 160ms ease',
            }}>
                <Box sx={{
                    width: 32, height: 32, borderRadius: '7px', flexShrink: 0,
                    bgcolor: complete ? c.go : c.asphalt,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'background-color 160ms ease',
                }}>
                    {complete
                        ? <CheckIcon sx={{ fontSize: 17, color: '#fff' }} />
                        : <Typography sx={{ fontFamily: mono, fontSize: '11.5px', color: c.amber }}>{String(index + 1).padStart(2, '0')}</Typography>}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '14.5px', fontWeight: 600, color: c.ink, lineHeight: 1.3 }}>
                        {doc.document_name}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: c.inkSoft }}>
                        {[doc.document_type, total > 1 ? `${total} images` : null, cameraOnly(doc) ? 'Camera only' : null].filter(Boolean).join(' · ')}
                    </Typography>
                </Box>
                <Typography sx={{
                    fontSize: '11px', fontWeight: 500, letterSpacing: '0.5px', textTransform: 'uppercase',
                    px: '8px', py: '3px', borderRadius: '4px', flexShrink: 0,
                    bgcolor: isRequired(doc) ? c.amberSoft : c.page,
                    color: isRequired(doc) ? c.amberDeep : c.inkSoft,
                }}>
                    {isRequired(doc) ? 'Required' : 'Optional'}
                </Typography>
            </Box>

            <Box sx={{ p: 2 }}>
                <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(auto-fill, minmax(170px, 1fr))' } }}>
                    {state.files.map((file, i) => (
                        <UploadSlot
                            key={i}
                            doc={doc}
                            index={i}
                            total={total}
                            file={file}
                            preview={state.previews[i]}
                            hasError={Boolean(error) && !file}
                            onSelect={(f) => onSelectFile(i, f)}
                            onRemove={() => onRemoveFile(i)}
                        />
                    ))}
                </Box>

                {(needsNumber(doc) || needsExpiry(doc)) && (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        {needsNumber(doc) && (
                            <Grid size={{ xs: 12, sm: needsExpiry(doc) ? 6 : 12 }}>
                                <FieldLabel required>Document number</FieldLabel>
                                <TextField fullWidth placeholder="As printed on the document"
                                    value={state.number} onChange={(e) => onField('number', e.target.value)} />
                            </Grid>
                        )}
                        {needsExpiry(doc) && (
                            <Grid size={{ xs: 12, sm: needsNumber(doc) ? 6 : 12 }}>
                                <FieldLabel required>Expiry date</FieldLabel>
                                <TextField fullWidth type="date" value={state.expiry}
                                    inputProps={{ min: new Date().toISOString().slice(0, 10) }}
                                    onChange={(e) => onField('expiry', e.target.value)} />
                            </Grid>
                        )}
                    </Grid>
                )}
                <ErrorText>{error}</ErrorText>
            </Box>
        </Box>
    );
};

// ---- Live summary ticket ---------------------------------------------------

const TicketRow = ({ label, value }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: '7px' }}>
        <Typography sx={{ fontSize: '12.5px', color: c.inkSoft }}>{label}</Typography>
        <Typography sx={{
            fontSize: '13px', fontWeight: 500, color: value ? c.ink : c.lineStrong, textAlign: 'right',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 190,
        }}>
            {value || '—'}
        </Typography>
    </Box>
);

const FleetTicket = ({ form, city, docsReady, docsTotal, completion }) => (
    <Box sx={{
        bgcolor: c.surface, border: `1px solid ${c.line}`, borderRadius: '12px', overflow: 'hidden',
        boxShadow: '0 1px 0 rgba(18,21,25,0.03), 0 16px 36px -24px rgba(18,21,25,0.25)',
    }}>
        <Box sx={{ bgcolor: c.asphalt, px: 3, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BadgeOutlinedIcon sx={{ fontSize: 18, color: c.amber }} />
                <Typography sx={{ fontSize: '12px', letterSpacing: '1.5px', textTransform: 'uppercase', color: c.onDarkMuted }}>
                    Fleet card
                </Typography>
            </Box>
            <Typography sx={{ fontFamily: mono, fontSize: '11px', color: c.amber, border: '1px solid rgba(242,179,61,0.4)', px: '6px', py: '1px', borderRadius: '4px' }}>
                PENDING
            </Typography>
        </Box>

        <Box sx={{ px: 3, pt: 2.5 }}>
            <Typography sx={{
                fontFamily: display, fontSize: '20px', fontWeight: 700, letterSpacing: '-0.3px',
                color: form.name ? c.ink : c.lineStrong, lineHeight: 1.2, wordBreak: 'break-word',
            }}>
                {form.name || 'Your fleet name'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, mt: 2.5 }}>
                <Typography sx={{ fontFamily: mono, fontSize: '34px', fontWeight: 500, color: c.ink, lineHeight: 1 }}>
                    {String(docsReady).padStart(2, '0')}
                </Typography>
                <Typography sx={{ fontFamily: mono, fontSize: '16px', color: c.inkSoft, mb: '3px' }}>
                    / {String(docsTotal).padStart(2, '0')}
                </Typography>
            </Box>
            <Typography sx={{ fontSize: '12px', color: c.inkSoft, mt: '6px' }}>
                {city ? 'Documents ready' : 'Choose a city to see documents count'}
            </Typography>
        </Box>

        <Box sx={{
            position: 'relative', my: 2.5, borderTop: `1.5px dashed ${c.line}`,
            '&::before, &::after': {
                content: '""', position: 'absolute', top: -11, width: 20, height: 20, borderRadius: '50%', bgcolor: c.page,
            },
            '&::before': { left: -11 }, '&::after': { right: -11 },
        }} />

        <Box sx={{ px: 3, pb: 2.5 }}>
            <TicketRow label="Email" value={form.email.trim()} />
            <TicketRow label="Phone" value={form.phone} />
            <TicketRow label="City" value={city?.city_name} />
            <TicketRow label="Currency" value={city ? `${city.currency} (${city.currency_symbol})` : ''} />
            <TicketRow label="Payout Destination" value={form.payoutDestination === 1 ? 'Fleet' : form.payoutDestination === 2 ? 'Driver' : '—'} />

            <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '6px' }}>
                    <Typography sx={{ fontSize: '12px', color: c.inkSoft }}>Registration complete</Typography>
                    <Typography sx={{ fontFamily: mono, fontSize: '12px', color: c.ink }}>{completion}%</Typography>
                </Box>
                <Box sx={{ height: 6, borderRadius: 3, bgcolor: c.page, overflow: 'hidden' }}>
                    <Box sx={{
                        height: '100%', width: `${completion}%`, borderRadius: 3,
                        bgcolor: completion === 100 ? c.go : c.amber,
                        transition: 'width 400ms ease, background-color 300ms ease',
                    }} />
                </Box>
            </Box>
        </Box>
    </Box>
);

// ---------------------------------------------------------------------------

const FleetSelfRegister = () => {
    useFonts();
    const navigate = useNavigate();

    const [form, setForm] = useState({ name: '', email: '', phone: '', payoutDestination:'' });
    const [errors, setErrors] = useState({});
    const [step, setStep] = useState(0);

    const [cities, setCities] = useState([]);
    const [citiesLoading, setCitiesLoading] = useState(true);
    const [citiesError, setCitiesError] = useState('');
    const [cityId, setCityId] = useState(null);

    const [docState, setDocState] = useState({});   // { [document_id]: { files, previews, number, expiry } }
    const [docErrors, setDocErrors] = useState({});
    const docStateRef = useRef(docState);
    docStateRef.current = docState;

    const [submitting, setSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const city = cities.find((ct) => ct.city_id === cityId) || null;
    const documents = city?.fleet_required_documents || [];
    const isLast = step === STEPS.length - 1;
    const serverUrl = config.serverUrl;
    const operationsUrl = config.operationsUrl;
    const [image, setImages] = useState('');
    const [successOpen, setSuccessOpen] = useState(false);
    const [submittedEmail, setSubmittedEmail] = useState('');

    // ---- Load cities + documents on first render ----
    const loadCities = async () => {
        setCitiesLoading(true);
        setCitiesError('');
        try {
            const url = `${serverUrl}/public/fleet/get_required_documents`;
            const response = await makeGetRequestToUrl(url, { business_token: BUSINESS_TOKEN });
            if (response?.flag === 200 && Array.isArray(response.data)) {
                setCities(response.data);
                if (response.data.length === 1) selectCity(response.data[0]);
            } else {
                setCitiesError(response?.message || 'We couldn\u2019t load cities.');
            }
        } catch (error) {
            console.error('Error loading cities:', error);
            setCitiesError('We couldn\u2019t load cities. Check your connection and try again.');
        } finally {
            setCitiesLoading(false);
        }
    };

    useEffect(() => { loadCities(); }, []);

    // Revoke image previews when the page unmounts
    useEffect(() => () => {
        Object.values(docStateRef.current).forEach((st) => st.previews.forEach((p) => p && URL.revokeObjectURL(p)));
    }, []);

    // ---- Field handlers ----
    const clearError = (field) => setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));

    const setField = (field) => (e) => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }));
        clearError(field);
    };

    const selectCity = (ct) => {
        if (ct.city_id === cityId) return;
        Object.values(docStateRef.current).forEach((st) => st.previews.forEach((p) => p && URL.revokeObjectURL(p)));
        setCityId(ct.city_id);
        setDocState(Object.fromEntries((ct.fleet_required_documents || []).map((d) => [d.document_id, emptyDocState(d)])));
        setDocErrors({});
        clearError('city');
    };

    const clearDocError = (id) => setDocErrors((prev) => (prev[id] ? { ...prev, [id]: undefined } : prev));

    const selectFile = async (doc, index, file) => {
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            setDocErrors((prev) => ({
                ...prev,
                [doc.document_id]: 'Please choose an image file (JPG, PNG, etc.)',
            }));
            return;
        }

        if (file.size > MAX_FILE_MB * 1024 * 1024) {
            setDocErrors((prev) => ({
                ...prev,
                [doc.document_id]: `Image must be under ${MAX_FILE_MB} MB`,
            }));
            return;
        }

        const oldPreview =
            docStateRef.current[doc.document_id]?.previews[index];

        if (oldPreview) {
            URL.revokeObjectURL(oldPreview);
        }

        const preview = URL.createObjectURL(file);

        // Show selected image immediately
        setDocState((prev) => {
            const cur = prev[doc.document_id] || emptyDocState(doc);

            const files = [...cur.files];
            const previews = [...cur.previews];

            files[index] = {
                name: file.name,
                src: '',
                file,
                isUploaded: false,
            };

            previews[index] = preview;

            return {
                ...prev,
                [doc.document_id]: {
                    ...cur,
                    files,
                    previews,
                },
            };
        });

        clearDocError(doc.document_id);

        // Upload image to S3
        const formData = new FormData();
        formData.append('image', file);

        try {
            const response = await axios.post(
                operationsUrl + '/upload_image',
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            const s3Url = response?.data?.url;

            if (!s3Url) {
                throw new Error('Image upload failed. No image URL received.');
            }

            // Store uploaded S3 URL
            setDocState((prev) => {
                const cur = prev[doc.document_id] || emptyDocState(doc);
                const files = [...cur.files];

                files[index] = {
                    name: file.name,
                    src: s3Url,
                    isUploaded: true,
                };

                return {
                    ...prev,
                    [doc.document_id]: {
                        ...cur,
                        files,
                    },
                };
            });

        } catch (error) {
            console.error('Error uploading document image:', error);

            setDocErrors((prev) => ({
                ...prev,
                [doc.document_id]:
                    error?.response?.data?.message ||
                    'Unable to upload image. Please try again.',
            }));

            // Remove failed upload
            setDocState((prev) => {
                const cur = prev[doc.document_id] || emptyDocState(doc);

                const files = [...cur.files];
                const previews = [...cur.previews];

                files[index] = null;
                previews[index] = null;

                return {
                    ...prev,
                    [doc.document_id]: {
                        ...cur,
                        files,
                        previews,
                    },
                };
            });

            URL.revokeObjectURL(preview);
        }
    };

    const removeFile = (doc, index) => {
        const oldPreview = docStateRef.current[doc.document_id]?.previews[index];
        if (oldPreview) URL.revokeObjectURL(oldPreview);
        setDocState((prev) => {
            const cur = prev[doc.document_id];
            const files = [...cur.files];
            const previews = [...cur.previews];
            files[index] = null;
            previews[index] = null;
            return { ...prev, [doc.document_id]: { ...cur, files, previews } };
        });
    };

    const setDocField = (doc, key, value) => {
        setDocState((prev) => ({ ...prev, [doc.document_id]: { ...prev[doc.document_id], [key]: value } }));
        clearDocError(doc.document_id);
    };

    // ---- Validation ----
    const validateDetails = () => {
        const e = {};
        if (!form.name.trim()) e.name = 'Enter your fleet or company name';
        if (!form.email.trim()) e.email = 'Enter an email address';
        else if (!EMAIL_REGEX.test(form.email.trim())) e.email = 'This email address looks invalid';
        if (!form.phone) e.phone = 'Enter a contact number';
        else if (!isValidPhoneNumber(form.phone)) e.phone = 'This number looks invalid';
        if (!form.payoutDestination) {
            e.payoutDestination = 'Select a payout destination';
        }
        return e;
    };

    const validateDocuments = () => {
        const dErr = {};
        documents.forEach((d) => {
            const msg = getDocError(d, docState[d.document_id]);
            if (msg) dErr[d.document_id] = msg;
        });
        return dErr;
    };

    const handleSuccessClose = () => {
        setSuccessOpen(false);
        Object.values(docStateRef.current).forEach((st) =>
            st.previews.forEach((p) => p && URL.revokeObjectURL(p))
        );
        setForm({ name: '', email: '', phone: '' });
        setErrors({});
        setCityId(null);
        setDocState({});
        setDocErrors({});
        goToStep(0);
    };
    const docsTotal = documents.filter(isRequired).length;
    const docsReady = documents.filter((d) => isRequired(d) && isDocComplete(d, docState[d.document_id])).length;

    const completion = useMemo(() => {
        const checks = [
            form.name.trim(), EMAIL_REGEX.test(form.email.trim()),
            form.phone && isValidPhoneNumber(form.phone), Boolean(cityId),
        ];
        const docPart = docsTotal ? docsReady / docsTotal : (cityId ? 1 : 0);
        return Math.round(((checks.filter(Boolean).length + docPart) / (checks.length + 1)) * 100);
    }, [form, cityId, docsReady, docsTotal]);

    // ---- Navigation ----
    const goToStep = (i) => {
        setStep(i);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const next = () => {
        const e = validateDetails();
        if (Object.keys(e).length) { setErrors(e); return; }
        goToStep(1);
    };

    // ---- Submit (multipart) ----
    // ---- Submit (JSON body) ----
    const submit = async () => {
        if (submitting) return;

        const detailErrors = validateDetails();

        if (Object.keys(detailErrors).length) {
            setErrors(detailErrors);
            goToStep(0);
            return;
        }

        if (!cityId) {
            setErrors((prev) => ({
                ...prev,
                city: 'Select the city you operate in',
            }));
            return;
        }

        const dErr = validateDocuments();

        if (Object.keys(dErr).length) {
            setDocErrors(dErr);

            const firstId = Object.keys(dErr)[0];

            document
                .getElementById(`doc-card-${firstId}`)
                ?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });

            return;
        }

const documentsPayload = documents
    .map((d) => {
        const st = docState[d.document_id];

        if (!st || !st.files.some(Boolean)) return null;

        // Collect every uploaded image URL for this document, in slot order
        // (front, back, ...) and join them with ";;;;"
        const imageUrls = st.files
            .filter((file) => file?.isUploaded && file?.src)
            .map((file) => file.src)
            .join(';;;;');

        return {
            document_id: d.document_id,
            document_number: st.number.trim() || undefined,
            images: imageUrls,
            expiry_date: st.expiry || undefined,
        };
    })
    .filter(Boolean);

        const requestBody = {
            name: form.name.trim(),
            email: form.email.trim(),
            phone_no: form.phone,
            city_id: cityId,
            business_token: BUSINESS_TOKEN,
            payout_destination: form.payoutDestination,
            documents: documentsPayload,
        };

        setSubmitting(true);

        try {
            const url = operationsUrl + '/fleet/register_fleet';

            const { data: response } = await axios.post(url, requestBody, {
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (response?.flag === 200) {
                setSnackbar({
                    open: true,
                    message: response.message || 'Fleet registered',
                    severity: 'success',
                });
                setSubmittedEmail(form.email.trim());
                setSuccessOpen(true);

            } else {
                setSnackbar({
                    open: true,
                    message:
                        response?.message ||
                        'We couldn’t complete your registration',
                    severity: 'error',
                });
            }
        } catch (error) {
            console.error('Error while registering fleet:', error);

            const msg =
                error?.response?.data?.message ||
                error?.message ||
                'Something went wrong. Please try again.';

            setSnackbar({
                open: true,
                message: msg,
                severity: 'error',
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    const phoneInputOverrides = `
        .fleet-phone .PhoneInputInput {
            border: none; outline: none; background: transparent;
            font-family: inherit; font-size: 15px; color: ${c.ink};
        }
        .fleet-phone .PhoneInputCountry { margin-left: 14px; margin-right: 4px; }
        .fleet-phone .PhoneInputCountrySelectArrow { color: ${c.inkSoft}; opacity: 1; }
    `;

    // ---- Step content ----
    const detailsStep = (
        <Grid container spacing={2.5}>
            <Grid size={12}>
                <FieldLabel required>Fleet / company name</FieldLabel>
                <TextField fullWidth placeholder="e.g. Super Fleet Delivery" autoComplete="organization"
                    value={form.name} onChange={setField('name')}
                    error={Boolean(errors.name)} helperText={errors.name} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
                <FieldLabel required>Email</FieldLabel>
                <TextField fullWidth type="email" placeholder="ops@yourfleet.com" autoComplete="email"
                    value={form.email} onChange={setField('email')}
                    error={Boolean(errors.email)} helperText={errors.email} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
                <FieldLabel required>Phone number</FieldLabel>
                <Box sx={{
                    border: `1px solid ${errors.phone ? c.error : c.line}`, borderRadius: '6px', height: '48px',
                    display: 'flex', alignItems: 'center', bgcolor: c.surface,
                    '&:hover': { borderColor: errors.phone ? c.error : c.lineStrong },
                    '&:focus-within': { borderColor: errors.phone ? c.error : c.asphalt },
                }}>
                    <PhoneInput
                        className="fleet-phone"
                        defaultCountry="IN"
                        international
                        value={form.phone}
                        onChange={(v) => { setForm((p) => ({ ...p, phone: v || '' })); clearError('phone'); }}
                        placeholder="Mobile number"
                        style={{ width: '100%', height: '100%' }}
                    />
                </Box>
                <ErrorText>{errors.phone}</ErrorText>
            </Grid>
            <Grid size={12}>
            <FieldLabel>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Payout destination <span style={{color:c.error}}>*</span>
                    <Tooltip title="Choose where trip earnings will be deposited: the central Fleet account or directly to the individual Driver's account.">
                        <InfoOutlinedIcon sx={{ fontSize: 16, color: c.inkSoft, cursor: 'pointer' }} />
                    </Tooltip>
                </Box>
            </FieldLabel>

                <FormControl fullWidth error={Boolean(errors.payoutDestination)}>
                    <Select
                        value={form.payoutDestination}
                        displayEmpty
                        onChange={(e) => {
                            setForm((prev) => ({
                                ...prev,
                                payoutDestination: e.target.value,
                            }));
                            clearError('payoutDestination');
                        }}
                        renderValue={(value) => {
                            if (!value) {
                                return (
                                    <Box component="span" sx={{ color: c.inkSoft }}>
                                        Select payout destination
                                    </Box>
                                );
                            }

                            return value === 1 ? 'Fleet' : 'Driver';
                        }}
                        sx={{
                            '& .MuiSelect-select': {
                                py: '13px',
                                px: '14px',
                                fontSize: '15px',
                            },
                        }}
                    >
                        <MenuItem value={1}>Fleet</MenuItem>
                        <MenuItem value={2}>Driver</MenuItem>
                    </Select>

                    <ErrorText>{errors.payoutDestination}</ErrorText>
                </FormControl>
            </Grid>
        </Grid>
    );
    const handleAddImage = async (event) => {
        const file = event.target.files[0];
        if (file) {
            const formData = new FormData();
            formData.append('image', file);

            try {
                const response = await axios.post(operationsUrl + '/upload_image', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });

                const s3Url = response.data.url;
                const newImage = {
                    name: file.name,
                    src: s3Url,
                    isUploaded: true,
                };

                setImages([newImage]); // Restrict to one image
            } catch (error) {
                console.error('Error uploading image:', error);
            }
        }
    };

    const documentsStep = (
        <Box>
            <FieldLabel required>Operating city</FieldLabel>
            {citiesLoading ? (
                <Skeleton variant="rounded" height={48} sx={{ borderRadius: '6px' }} />
            ) : citiesError ? (
                <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
                    p: 2, borderRadius: '8px', bgcolor: c.errorSoft, border: `1px solid ${c.error}`,
                }}>
                    <Typography sx={{ fontSize: '13.5px', color: c.ink }}>{citiesError}</Typography>
                    <Button onClick={loadCities} startIcon={<RefreshIcon />} sx={{ textTransform: 'none', color: c.ink, flexShrink: 0 }}>
                        Retry
                    </Button>
                </Box>
            ) : (
                <FormControl fullWidth error={Boolean(errors.city)}>
                    <Select
                        displayEmpty
                        value={cityId ?? ''}
                        onChange={(e) => {
                            const ct = cities.find((x) => x.city_id === e.target.value);
                            if (ct) selectCity(ct);
                        }}
                        inputProps={{ 'aria-label': 'Operating city' }}
                        renderValue={(val) => {
                            const ct = cities.find((x) => x.city_id === val);
                            return ct
                                ? ct.city_name
                                : <Box component="span" sx={{ color: c.inkSoft }}>Select your operating city</Box>;
                        }}
                        MenuProps={{ PaperProps: { sx: { maxHeight: 280, borderRadius: '8px', mt: 0.5 } } }}
                        sx={{ '& .MuiSelect-select': { py: '13px', px: '14px', fontSize: '15px' } }}
                    >
                        {cities.map((ct) => (
                            <MenuItem key={ct.city_id} value={ct.city_id}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                                    <Typography sx={{ fontSize: '14.5px' }}>{ct.city_name}</Typography>
                                    <Typography sx={{ fontFamily: mono, fontSize: '11.5px', color: c.inkSoft }}>
                                        {ct.currency}
                                    </Typography>
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}
            <ErrorText>{errors.city}</ErrorText>
            <ErrorText>{errors.city}</ErrorText>

            {city && (
                <Box sx={{ mt: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1.5 }}>
                        <Typography sx={{ fontFamily: display, fontSize: '17px', fontWeight: 600, color: c.ink }}>
                            Documents for {city.city_name}
                        </Typography>
                        {docsTotal > 0 && (
                            <Typography sx={{ fontFamily: mono, fontSize: '12.5px', color: docsReady === docsTotal ? c.go : c.inkSoft }}>
                                {docsReady}/{docsTotal} required ready
                            </Typography>
                        )}
                    </Box>

                    {documents.length === 0 ? (
                        <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 1.5, p: 2.5, borderRadius: '10px',
                            bgcolor: c.goSoft, border: `1px solid ${c.go}`,
                        }}>
                            <DescriptionOutlinedIcon sx={{ color: c.go }} />
                            <Typography sx={{ fontSize: '13.5px', color: c.ink }}>
                                No documents are needed for {city.city_name} right now. You can submit your registration.
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {documents.map((doc, i) => (
                                <Box key={doc.document_id} id={`doc-card-${doc.document_id}`}>
                                    <DocumentCard
                                        doc={doc}
                                        index={i}
                                        state={docState[doc.document_id] || emptyDocState(doc)}
                                        error={docErrors[doc.document_id]}
                                        onSelectFile={(idx, f) => selectFile(doc, idx, f)}
                                        onRemoveFile={(idx) => removeFile(doc, idx)}
                                        onField={(key, v) => setDocField(doc, key, v)}
                                    />
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );

    return (
        <>

            <ThemeProvider theme={theme}>
                <style>{phoneInputOverrides}</style>

                <Box sx={{
                    minHeight: '100dvh', bgcolor: c.page,
                    backgroundImage: 'radial-gradient(rgba(18,21,25,0.07) 1px, transparent 1px)',
                    backgroundSize: '22px 22px',
                }}>
                    {/* ---- Header band ---- */}
                    <Box sx={{ bgcolor: c.asphalt, color: c.onDark, pb: { xs: 9, md: 11 }, position: 'relative', overflow: 'hidden' }}>
                        <Box aria-hidden sx={{
                            position: 'absolute', inset: 0, pointerEvents: 'none',
                            background: 'radial-gradient(60% 120% at 100% 0%, rgba(242,179,61,0.09) 0%, transparent 60%)',
                        }} />
                        <Box sx={{ borderBottom: `1px solid ${c.onDarkLine}`, position: 'relative' }}>
                            <Box sx={{
                                maxWidth: 1120, mx: 'auto', px: { xs: 2.5, sm: 4 }, height: 64,
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            }}>
                                {/* TODO: swap for your logo */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                    <Box sx={{ width: 30, height: 30, borderRadius: '7px', bgcolor: c.amber, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <DirectionsCarIcon sx={{ fontSize: 18, color: c.asphalt }} />
                                    </Box>
                                    <Typography sx={{ fontFamily: display, fontWeight: 700, fontSize: '16px' }}>Fleet Partner</Typography>
                                </Box>
                                <Typography sx={{ fontSize: '13px', color: c.onDarkMuted }}>
                                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Already Registered? </Box>
                                    <Box
                                        component="a"
                                        href="/fleet/registration-status"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            navigate('/fleet/registration-status');
                                        }}
                                        sx={{ color: c.onDark, fontWeight: 500, textDecoration: 'underline', textUnderlineOffset: '3px' }}
                                    >
                                        View Status.
                                    </Box>
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ maxWidth: 1120, mx: 'auto', px: { xs: 2.5, sm: 4 }, pt: { xs: 4, md: 5 }, position: 'relative' }}>
                            <Typography sx={{ fontFamily: mono, fontSize: '12px', letterSpacing: '2px', textTransform: 'uppercase', color: c.amber }}>
                                Fleet partner registration
                            </Typography>
                            <Typography component="h1" sx={{
                                fontFamily: display, fontWeight: 700, fontSize: { xs: '30px', md: '38px' },
                                letterSpacing: '-1px', lineHeight: 1.1, mt: 1.5,
                            }}>
                                Put your vehicles to work.
                            </Typography>
                            <Typography sx={{ fontSize: '15px', color: c.onDarkMuted, mt: 1.5, maxWidth: 520, lineHeight: 1.6 }}>
                                Two quick stops: your details, then your city and documents.
                            </Typography>
                            <RouteStepper current={step} onJump={(i) => i < step && goToStep(i)} />
                        </Box>
                    </Box>

                    {/* ---- Content ---- */}
                    <Box sx={{ maxWidth: 1120, mx: 'auto', px: { xs: 2.5, sm: 4 }, mt: { xs: -6, md: -8 }, pb: 8, position: 'relative' }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 340px' }, gap: 4, alignItems: 'start' }}>

                            <Box sx={{
                                bgcolor: c.surface, border: `1px solid ${c.line}`, borderRadius: '12px',
                                boxShadow: '0 1px 0 rgba(18,21,25,0.03), 0 20px 44px -28px rgba(18,21,25,0.3)',
                            }}>
                                <Box sx={{ px: { xs: 3, sm: 4 }, pt: { xs: 3, sm: 4 }, pb: 3, borderBottom: `1px solid ${c.line}` }}>
                                    <Typography sx={{ fontFamily: mono, fontSize: '12px', color: c.amberDeep, letterSpacing: '1px' }}>
                                        STOP {String(step + 1).padStart(2, '0')} / {String(STEPS.length).padStart(2, '0')}
                                    </Typography>
                                    <Typography component="h2" sx={{ fontFamily: display, fontSize: '23px', fontWeight: 700, color: c.ink, letterSpacing: '-0.4px', mt: 0.75 }}>
                                        {STEPS[step].title}
                                    </Typography>
                                    <Typography sx={{ fontSize: '14px', color: c.inkSoft, mt: 0.75 }}>
                                        {STEPS[step].description}
                                    </Typography>
                                </Box>

                                <Box key={step} sx={{
                                    px: { xs: 3, sm: 4 }, py: 3.5,
                                    '@keyframes stepIn': { from: { opacity: 0, transform: 'translateX(12px)' }, to: { opacity: 1, transform: 'none' } },
                                    animation: 'stepIn 260ms ease-out',
                                    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                                }}>
                                    {step === 0 ? detailsStep : documentsStep}
                                </Box>

                                <Box sx={{
                                    px: { xs: 3, sm: 4 }, py: 2.5, borderTop: `1px solid ${c.line}`, bgcolor: '#FAFAF8',
                                    borderRadius: '0 0 12px 12px',
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
                                }}>
                                    {step > 0 ? (
                                        <Button
                                            onClick={() => goToStep(step - 1)}
                                            disabled={submitting}
                                            startIcon={<ArrowBackIcon sx={{ fontSize: '18px !important' }} />}
                                            sx={{ textTransform: 'none', color: c.inkSoft, fontWeight: 500, fontSize: '14.5px', '&:hover': { bgcolor: c.page, color: c.ink } }}
                                        >
                                            Back
                                        </Button>
                                    ) : <Box />}

                                    <Button
                                        variant="contained"
                                        disableElevation
                                        disabled={submitting || (isLast && (!cityId || citiesLoading))}
                                        onClick={isLast ? submit : next}
                                        endIcon={!submitting && <ArrowForwardIcon sx={{ fontSize: '18px !important', color: c.amber }} />}
                                        sx={{
                                            bgcolor: c.asphalt, color: '#fff', textTransform: 'none', fontWeight: 500,
                                            fontSize: '15px', borderRadius: '6px', minWidth: { xs: 160, sm: 200 }, height: 48, px: 3,
                                            '&:hover': { bgcolor: c.asphaltDeep },
                                            '&.Mui-disabled': { bgcolor: c.asphalt, color: '#fff', opacity: 0.55 },
                                        }}
                                    >
                                        {submitting ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                                <CircularProgress size={18} sx={{ color: '#fff' }} />
                                                <span>{uploadProgress < 100 ? `Uploading ${uploadProgress}%` : 'Finishing up\u2026'}</span>
                                            </Box>
                                        ) : isLast ? 'Submit registration' : 'Continue to documents'}
                                    </Button>
                                </Box>
                            </Box>

                            <Box sx={{ position: { md: 'sticky' }, top: { md: 24 } }}>
                                <FleetTicket form={form} city={city} docsReady={docsReady} docsTotal={docsTotal} completion={completion} />
                            </Box>
                        </Box>
                    </Box>
                </Box>

                <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleSnackbarClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                    <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%', borderRadius: '8px' }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </ThemeProvider>
            <Dialog
                open={successOpen}
                onClose={(e, reason) => {
                    // force an explicit click on the button
                    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                    handleSuccessClose();
                }}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: '14px', overflow: 'hidden' } }}
                aria-labelledby="fleet-success-title"
                aria-describedby="fleet-success-desc"
            >
                <Box sx={{ bgcolor: c.asphalt, py: 3.5, display: 'flex', justifyContent: 'center' }}>
                    <Box sx={{
                        width: 64, height: 64, borderRadius: '50%', bgcolor: c.amber,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 10px 24px -8px rgba(242,179,61,0.7)',
                    }}>
                        <HourglassTopIcon sx={{ fontSize: 32, color: c.asphalt }} />
                    </Box>
                </Box>

                <DialogContent sx={{ textAlign: 'center', px: { xs: 3, sm: 4 }, pt: 3.5, pb: 4 }}>
                    <Typography id="fleet-success-title" component="h2"
                        sx={{ fontFamily: display, fontSize: '22px', fontWeight: 700, color: c.ink, letterSpacing: '-0.3px' }}>
                        Registration received
                    </Typography>

                    <Typography id="fleet-success-desc"
                        sx={{ fontSize: '14.5px', color: c.inkSoft, mt: 1.25, lineHeight: 1.6 }}>
                        We are verifying your documents and will update you soon.
                    </Typography>

                    <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 1.25, textAlign: 'left',
                        mt: 2.5, p: 1.75, borderRadius: '8px', bgcolor: c.amberSoft,
                    }}>
                        <MailOutlineIcon sx={{ color: c.amberDeep, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '13px', color: c.ink, lineHeight: 1.5 }}>
                            Until then, you can check the status of your fleet using your email id{' '}
                            <Box component="span" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
                                {submittedEmail}
                            </Box>.
                        </Typography>
                    </Box>

                    <Button
                        fullWidth
                        variant="contained"
                        disableElevation
                        onClick={handleSuccessClose}
                        sx={{
                            mt: 3, height: 48, textTransform: 'none', fontWeight: 500, fontSize: '15px',
                            bgcolor: c.asphalt, color: '#fff', borderRadius: '6px',
                            '&:hover': { bgcolor: c.asphaltDeep },
                        }}
                    >
                        Done
                    </Button>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default FleetSelfRegister;